import { BookOpen, Check, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../lib/api';
import type { Child } from '../lib/types';
import { POPULAR_SUBJECTS } from '../ui/SubjectField';
import { GradeSelect } from '../ui/GradeSelect';
import { ColorPicker } from './Dashboard';
import { useParent } from './ParentContext';

/** The student's profile: name, age, grade and color, subjects, and removing them. Their settings are on Features (#833). */
export function StudentProfile({ child }: { child: Child }) {
  const { replaceChild, refresh, toast } = useParent();
  const navigate = useNavigate();
  const [name, setName] = useState(child.name);
  const [color, setColor] = useState(child.color);
  const [age, setAge] = useState(child.age ? String(child.age) : '');
  const [grade, setGrade] = useState(child.grade ?? '');
  // For state paperwork (#954): only while the family has state compliance on, and then required, with the grade.
  const sc = child.stateCompliance;
  const stateOn = !!sc?.on;
  const [legalName, setLegalName] = useState(sc?.legalName ?? '');
  const [birthDate, setBirthDate] = useState(sc?.birthDate ?? '');
  const dirty =
    name.trim() !== child.name ||
    color !== child.color ||
    age !== (child.age ? String(child.age) : '') ||
    grade !== (child.grade ?? '') ||
    (stateOn && (legalName.trim() !== (sc?.legalName ?? '') || birthDate !== (sc?.birthDate ?? '')));
  const stateMissing = stateOn && (!legalName.trim() || !birthDate || !grade.trim());

  async function save() {
    try {
      const ageNum = age.trim() === '' ? null : Math.max(0, Math.min(25, Math.round(Number(age) || 0)));
      const state = stateOn ? { legalName: legalName.trim(), birthDate } : {};
      replaceChild(await api.patch<Child>(`/api/children/${child.id}`, { name: name.trim(), color, age: ageNum, grade: grade.trim(), ...state }));
      toast({ text: 'Saved.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  async function remove() {
    if (!confirm(`Remove ${child.name}? This deletes their schedule, rules, activity, records, documents and paired devices.`)) return;
    await api.del(`/api/children/${child.id}`);
    await refresh();
    navigate('/');
  }

  return (
    <>
      <section className="card stack">
        <h2>About {child.name}</h2>
        <label>
          Name / Nickname
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
        </label>
        <div className="field-row">
          <label>
            Age <span className="muted small">(optional)</span>
            <input type="number" min={0} max={25} value={age} onChange={(e) => setAge(e.target.value)} placeholder="—" />
          </label>
          <label>
            Grade {stateOn ? <span className="muted small">(required for state paperwork)</span> : <span className="muted small">(optional)</span>}
            <GradeSelect value={grade} onChange={setGrade} required={stateOn} />
          </label>
        </div>
        <ColorPicker value={color} onChange={setColor} />
      {stateOn && (
        <div className="stack state-paperwork">
          <div className="row-between wrap">
            <h3>For state paperwork</h3>
            <span className="tag">State compliance on</span>
          </div>
          <p className="muted small">
            Used only on the notices, plans, reports and transcripts you make. Shown only to you and the adults you share {child.name} with, never to {child.name}.
            Turning off state compliance deletes them.
          </p>
          <div className="field-row">
            <label className="grow">
              Full legal name <span className="muted small">(required)</span>
              <input value={legalName} onChange={(e) => setLegalName(e.target.value)} maxLength={160} required aria-required="true" autoComplete="off" />
            </label>
            <label>
              Date of birth <span className="muted small">(required)</span>
              <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} required aria-required="true" />
            </label>
          </div>
          {stateMissing && <p className="warn-text small">State compliance needs {child.name}’s full legal name, date of birth and grade before documents can be made.</p>}
        </div>
      )}
        <div className="row-between">
          <button className="btn danger-text" onClick={remove}>
            Remove {child.name}
          </button>
          <button className="btn primary" onClick={save} disabled={!dirty || !name.trim() || stateMissing}>
            Save
          </button>
        </div>
      </section>
      <SubjectsSection child={child} />
    </>
  );
}

/**
 * The child's subjects (#247): tap popular homeschool subjects or add your own. They fill the Subject lists in the
 * planner, lesson plans and grade book. Each change saves right away.
 */
function SubjectsSection({ child }: { child: Child }) {
  const { replaceChild, toast } = useParent();
  const subjects = child.subjects ?? [];
  const [custom, setCustom] = useState('');
  const has = (s: string) => subjects.some((t) => t.toLowerCase() === s.toLowerCase());
  // Popular subjects first, then the family's own ones.
  const chips = [...POPULAR_SUBJECTS, ...subjects.filter((s) => !POPULAR_SUBJECTS.some((p) => p.toLowerCase() === s.toLowerCase()))];

  async function save(next: string[]) {
    try {
      replaceChild(await api.patch<Child>(`/api/children/${child.id}`, { subjects: next }));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  const toggle = (s: string) => save(has(s) ? subjects.filter((t) => t.toLowerCase() !== s.toLowerCase()) : [...subjects, s]);
  function addCustom() {
    const s = custom.trim();
    if (!s) return;
    setCustom('');
    if (!has(s)) void save([...subjects, s]);
  }

  return (
    <section className="card stack">
      <h2 className="with-icon">
        <BookOpen size={18} /> Subjects
      </h2>
      <p className="muted small">
        Pick the subjects {child.name} studies. They fill the Subject lists in {child.name}’s planner, lesson plans and grade book.
        {subjects.length > 0 && (
          <>
            {' '}
            <strong>{subjects.length}</strong> picked.
          </>
        )}
      </p>
      <div className="chips subject-chips" role="group" aria-label="Subjects">
        {chips.map((s) => {
          const on = has(s);
          const isCustom = !POPULAR_SUBJECTS.includes(s);
          return (
            <button key={s} type="button" className={`chip ${on ? 'active' : ''}`} aria-pressed={on} onClick={() => toggle(s)} title={on && isCustom ? `Remove ${s}` : undefined}>
              {on ? isCustom ? <X size={14} /> : <Check size={14} /> : <Plus size={14} />}
              {s}
            </button>
          );
        })}
      </div>
      <div className="field-row export-row">
        <label className="grow">
          Add your own subject
          <input
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCustom()}
            maxLength={60}
            placeholder="e.g. Nature Study"
          />
        </label>
        <button className="btn" onClick={addCustom} disabled={!custom.trim() || subjects.length >= 40}>
          <Plus size={15} /> Add
        </button>
      </div>
      {/* Core subjects (#607): used where the state counts core hours (e.g. Missouri: 600 of 1,000). */}
      {subjects.length > 0 && (
        <div className="stack">
          <span className="field-label">Which count as core</span>
          <span className="muted small">Only used where your state counts core hours. The usual ones are marked for you.</span>
          <div className="chips" role="group" aria-label="Core subjects">
            {subjects.map((s) => {
              const core = (child.coreSubjects ?? []).some((c) => c.toLowerCase() === s.toLowerCase());
              return (
                <button
                  key={s}
                  type="button"
                  className={`chip ${core ? 'active' : ''}`}
                  aria-pressed={core}
                  onClick={async () => {
                    const cur = child.coreSubjects ?? [];
                    const next = core ? cur.filter((c) => c.toLowerCase() !== s.toLowerCase()) : [...cur, s];
                    try {
                      replaceChild(await api.patch<Child>(`/api/children/${child.id}`, { coreSubjects: next }));
                    } catch (e) {
                      toast({ text: (e as Error).message, tone: 'alert' });
                    }
                  }}
                >
                  {core ? <Check size={14} /> : <Plus size={14} />} {s}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
